import {
  CheckOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import type { CellContext } from '@tanstack/react-table'
import { createColumnHelper } from '@tanstack/react-table'
import { Button, Tooltip } from 'antd'

import DeliveryTime from '@/components/Atoms/DeliveryTime/DeliveryTime'
import QuantityToggler from '@/components/Cart/QuantityToggler/QuantityToggler'
import PriceUnavailable from '@/components/Feed/PriceUnavailable/PriceUnavailable'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormattedFeedProductType,
  SegmentInterface,
  SegmentShortModel,
} from '@/types'
import { isBuyerUser } from '@/utils/auth/userRoles'
import { formatPriceWithVat } from '@/utils/price/vat'
import getProductKey from '@/utils/products/getProductKey'
import { getOrderSelectionBlockText } from '@/utils/products/offerAvailability'
import { getRowOfferPrice } from '@/utils/products/offerPrice'

import cn from './OrderProductsSelectionModal.module.scss'

export interface SelectedItem {
  product: FormattedFeedProductType
  quantity: number
  productKey: string
}

type SelectionRow = FormattedFeedProductType | SelectedItem

const getRecord = (dataItem: SelectionRow): FormattedFeedProductType =>
  'product' in dataItem ? dataItem.product : dataItem

const SegmentPriceHeader = () => {
  const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))
  const isVisible = useFeedStore((state) => state.isSegmentPriceVisible)
  const toggle = useFeedStore((state) => state.setIsSegmentPriceVisible)

  if (isClient) return <>Цена</>

  return (
    <div
      className={cn.priceHeader}
      onClick={(e) => {
        e.stopPropagation()
        toggle(!isVisible)
      }}
    >
      Цена {isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
    </div>
  )
}

const SegmentPriceCell = ({
  info,
}: {
  info: CellContext<SelectionRow, unknown>
  contextSegment?: SegmentShortModel | SegmentInterface | null
}) => {
  const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))
  const isVisible = useFeedStore((state) => state.isSegmentPriceVisible)

  const price = getRowOfferPrice(getRecord(info.row.original))

  if (!isClient && !isVisible) {
    return <span className={cn.cellPrice}>***</span>
  }

  if (price?.sellingPrice === undefined) {
    return (
      <span className={cn.cellPrice}>
        <PriceUnavailable price={price} />
      </span>
    )
  }

  return (
    <span className={`${cn.cellPrice} ${cn.cellStrong}`}>
      {formatPriceWithVat(price.sellingPrice)}
    </span>
  )
}

const getBaseColumns = <TRow extends SelectionRow>(
  contextSegment: SegmentShortModel | SegmentInterface | null,
  expandedRows?: Set<number>,
  toggleRow?: (code: number) => void,
  existingProductsMap?: Map<string, number>,
  showChildMeta = false
) => {
  const columnHelper = createColumnHelper<TRow>()

  return [
    columnHelper.display({
      id: 'article',
      header: 'Артикул',
      cell: (info) => {
        const record = getRecord(info.row.original)
        const isChild = record.parent === false

        return (
          <div className={cn.articleCell}>
            <span className={isChild ? cn.cellText : cn.cellTextBold}>
              {isChild && !showChildMeta ? '' : record.nomenclatureDto?.article}
            </span>
          </div>
        )
      },
    }),

    columnHelper.display({
      id: 'brand',
      header: 'Бренд',
      cell: (info) => {
        const record = getRecord(info.row.original)
        if (record.parent === false && !showChildMeta) return null

        return (
          <span className={cn.cellStrong}>
            {record.nomenclatureDto?.brandDto?.description}
          </span>
        )
      },
    }),

    columnHelper.display({
      id: 'name',
      header: 'Наименование',
      cell: (info) => {
        const record = getRecord(info.row.original)
        const val = record.nomenclatureDto?.description

        const qtyInOrder = existingProductsMap?.get(
          String(getProductKey(record))
        )

        const isParent = record.parent !== false
        const canExpand = Boolean(record.children?.length && toggleRow)
        const isExpanded = expandedRows?.has(record.nomenclatureDto.code)
        const canCollapse = Boolean(record.lastInChildren && toggleRow)

        return (
          <Tooltip title={isParent ? val : undefined} mouseEnterDelay={0.5}>
            <div className={cn.cellNameWrapper}>
              {(isParent || showChildMeta) && (
                <div className={cn.productNameText}>{val}</div>
              )}

              {canExpand && !isExpanded && (
                <button
                  type="button"
                  className={cn.otherOffersLink}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleRow?.(record.nomenclatureDto.code)
                  }}
                  aria-expanded={false}
                >
                  Остальные предложения...
                </button>
              )}

              {canCollapse && (
                <button
                  type="button"
                  className={cn.otherOffersLink}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleRow?.(record.nomenclatureDto.code)
                  }}
                  aria-expanded
                >
                  Свернуть
                </button>
              )}

              {qtyInOrder !== undefined && qtyInOrder > 0 && (
                <div className={cn.alreadyInOrderBadge}>
                  В заказе: <b>{qtyInOrder}</b> шт.
                </div>
              )}
            </div>
          </Tooltip>
        )
      },
    }),

    columnHelper.display({
      id: 'stock',
      header: 'Наличие',
      cell: (info) => {
        const record = getRecord(info.row.original)
        const stock = record.suppliersStock?.[0]?.stock?.[0]
        const unit =
          record.nomenclatureDto.unitOfMeasurementDto?.description || ''
        return (
          <span
            className={cn.cellStrong}
          >{`${stock?.quantity || 0} ${unit}`}</span>
        )
      },
    }),

    columnHelper.display({
      id: 'delivery',
      header: 'Срок',
      cell: (info) => {
        const s = getRecord(info.row.original).suppliersStock?.[0]?.stock?.[0]
        return (
          <span className={cn.cellText}>
            <DeliveryTime
              minDays={s?.effectiveEtaMinDays}
              maxDays={s?.effectiveEtaMaxDays}
            />
          </span>
        )
      },
    }),

    columnHelper.display({
      id: 'price',
      header: () => <SegmentPriceHeader />,
      cell: (info) => (
        <SegmentPriceCell
          info={info as unknown as CellContext<SelectionRow, unknown>}
          contextSegment={contextSegment}
        />
      ),
    }),

    columnHelper.display({
      id: 'store',
      header: 'Склад',
      cell: (info) => (
        <span className={cn.cellStrong}>
          {getRecord(info.row.original).suppliersStock?.[0]?.stock?.[0]
            ?.storeShortModel?.description || '-'}
        </span>
      ),
    }),

    columnHelper.display({
      id: 'rrc',
      header: 'РРЦ',
      cell: (info) => {
        const val = getRowOfferPrice(getRecord(info.row.original))?.retailPrice
        return (
          <span className={cn.cellPrice}>
            {val ? formatPriceWithVat(val) : '-'}
          </span>
        )
      },
    }),
  ]
}

interface SearchColumnsParams {
  basket: Map<string, SelectedItem>
  toggleInBasket: (record: FormattedFeedProductType) => void
  contextSegment: SegmentShortModel | SegmentInterface | null
  expandedRows: Set<number>
  toggleRow: (code: number) => void
  existingProductsMap?: Map<string, number>
}

interface BasketColumnsParams {
  updateQuantity: (key: string, quantity: number) => void
  toggleInBasket: (record: FormattedFeedProductType) => void
  contextSegment: SegmentShortModel | SegmentInterface | null
}

export const getSearchColumns = (params: SearchColumnsParams) => {
  const columnHelper = createColumnHelper<FormattedFeedProductType>()

  return [
    ...getBaseColumns<FormattedFeedProductType>(
      params.contextSegment,
      params.expandedRows,
      params.toggleRow,
      params.existingProductsMap
    ),
    columnHelper.display({
      id: 'actions',
      header: 'Заказ',
      cell: (info) => {
        const record = info.row.original
        const key = String(getProductKey(record))
        const isSelected = params.basket.has(key)
        const blockText = getOrderSelectionBlockText(
          record.suppliersStock[0]?.stock[0],
          getRowOfferPrice(record)
        )
        const isUnavailable = blockText !== undefined

        return (
          <div className={cn.actionsCell}>
            {isSelected ? (
              <CheckOutlined className={cn.selectedIcon} />
            ) : (
              <Tooltip title={blockText}>
                <span>
                  <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation()
                      params.toggleInBasket(record)
                    }}
                    size="small"
                    disabled={isUnavailable}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        )
      },
    }),
  ]
}

export const getBasketColumns = (params: BasketColumnsParams) => {
  const columnHelper = createColumnHelper<SelectedItem>()

  return [
    ...getBaseColumns<SelectedItem>(
      params.contextSegment,
      undefined,
      undefined,
      undefined,
      true
    ),
    columnHelper.display({
      id: 'actions',
      header: 'Заказ',
      cell: (info) => {
        const record = info.row.original.product
        const key = info.row.original.productKey
        const qty = info.row.original.quantity

        return (
          <div className={cn.actionsCell}>
            <div onClick={(e) => e.stopPropagation()}>
              <QuantityToggler
                location="orderSelection"
                element={record}
                productQuantity={qty}
                maxQuantity={
                  record.suppliersStock?.[0]?.stock?.[0]?.quantity || 0
                }
                onQuantityChange={(val) => params.updateQuantity(key, val)}
              />
            </div>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                params.toggleInBasket(record)
              }}
              size="small"
            />
          </div>
        )
      },
    }),
  ]
}
